import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Seo, Reveal } from "../components/Seo";
import { PageHero, SectionHead, CtaBand } from "../components/blocks";
import { PRODUCTS, SERVICES } from "../lib/site";
import NotFound from "./NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <NotFound />;

  const relatedProducts = PRODUCTS.filter((p) => s.products.includes(p.slug));

  return (
    <>
      <Seo
        title={`${s.name} — Enterprise Services | Softline Nigeria`}
        description={s.short}
        path={`/services/${s.slug}`}
      />
      <PageHero
        eyebrow={`Enterprise service · ${s.num}`}
        title={[s.name]}
        lead={s.short}
        crumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: s.name }]}
      />

      <section className="bg-white" data-testid="service-challenge">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHead dark={false} eyebrow="The business challenge" title="Why organisations call us." lead={s.challenge} />
          </div>
          <div>
            <SectionHead dark={false} eyebrow="Our proposition" title="What we deliver." lead={s.proposition} />
          </div>
        </div>
      </section>

      <section className="bg-surface" data-testid="service-capabilities">
        <div className="shell py-24 md:py-32">
          <SectionHead dark={false} eyebrow="Technical capabilities" title="Scope of delivery." />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10">
            {s.capabilities.map((c, i) => (
              <Reveal key={c} delay={i * 0.04} className="h-full">
                <div className="h-full bg-surface p-7 flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-lime mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-[15px] text-charcoal/80 leading-relaxed">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-charcoal noise-bg" data-testid="service-methodology">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHead dark eyebrow="Delivery methodology" title="How the engagement runs." lead={s.methodology} />
          </div>
          <div>
            <SectionHead
              dark
              eyebrow="Technologies & platforms"
              title="What we work with."
              lead="Representative platforms and standards — specific stacks are agreed per engagement."
            />
          </div>
        </div>
        <div className="shell pb-24 -mt-8">
          <div className="flex flex-wrap gap-3">
            {s.technologies.map((t) => (
              <span key={t} className="border border-white/20 text-softgrey px-4 py-2 text-sm" data-testid="service-tech-chip">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="service-deliverables">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <SectionHead dark={false} eyebrow="Typical deliverables" title="What you receive." />
            <ul className="mt-10 space-y-4">
              {s.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-[15px] text-charcoal/80">
                  <CheckCircle2 size={18} className="text-lime mt-0.5 shrink-0" aria-hidden="true" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 space-y-8">
            <Reveal delay={0.1}>
              <div className="border border-charcoal/10 p-8" data-testid="service-related-industries">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-charcoal/60">Related industries</p>
                <ul className="mt-5 space-y-2">
                  {s.industries.map((i) => (
                    <li key={i} className="text-[15px] text-charcoal/75">
                      {i}
                    </li>
                  ))}
                </ul>
                <Link to="/industries" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-charcoal hover:gap-3 transition-[gap] duration-200" data-testid="service-industries-link">
                  All industries <ArrowUpRight size={15} />
                </Link>
              </div>
            </Reveal>
            {relatedProducts.length ? (
              <Reveal delay={0.16}>
                <div className="bg-charcoal text-white p-8" data-testid="service-related-products">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">Related products</p>
                  <ul className="mt-5 space-y-2">
                    {relatedProducts.map((rp) => (
                      <li key={rp.slug}>
                        <Link to={`/products/${rp.slug}`} data-testid={`service-product-link-${rp.slug}`} className="inline-flex items-center gap-2 text-[15px] text-softgrey hover:text-lime transition-colors duration-200">
                          {rp.name} <ArrowUpRight size={14} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ) : null}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
